import { EditIcon, HelpIcon, LockIcon, SaveIcon } from "assets/icons";
import Divider from "components/BaseWidgets/divider";
import Input from "components/BaseWidgets/form/input";
import CustomTextArea from "components/BaseWidgets/form/textArea";
import Select from "components/BaseWidgets/select";
import GoPremiumCard from "components/CustomWidgets/goPremiumCard";
import {
  editCurrentLocationDetails,
  editPermanentAddressDetails,
  selectEditOptions,
} from "components/PersonProfile/helper";
import { useEffect, useState } from "react";
import "./style.scss";
import Button from "components/BaseWidgets/button";
import {
  getBasicDetails,
  getCurrentLocationDetails,
  getPermanentAddressDetails,
} from "./helper";
import {
  useCommunities,
  useDistrict,
  useSelectOptions,
  useStates,
  useTown,
} from "hooks/api/useListing";
import { mapToSelectOptions } from "utils/formatHelper";
import { smoking, drinking } from "components/Onboard/components/helper";
import DatePicker from "components/BaseWidgets/datePicker";
import dayjs from "dayjs";
import { displayTextOrDash } from "utils/formatHelper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { retrieveToken } from "utils/storage";
import Tooltip from "components/BaseWidgets/toolTip";

function AboutPanel({
  isEdit,
  hasPremium,
  id,
  profile,
  preview,
  blurredField = [],
}) {
  const lifestyleMap = {
    1: "Yes",
    2: "No",
    3: "Occasionally",
  };
  const booleanMap = {
    1: "No",
    0: "Yes",
  };
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState({
    about: false,
    basic: false,
    location: false,
    permanentAddress: false,
  });

  const fieldKeyMap = {
    "Grew up in": "City",
  };

  const isFieldBlurred = (label) => {
    const backendKey = fieldKeyMap[label] || label;
    const lowerLabel = label.toLowerCase();
    const lowerBackendKey = backendKey.toLowerCase();

    return blurredField.some((f) => {
      const lowerF = f.toLowerCase();
      return lowerF === lowerLabel || lowerF === lowerBackendKey;
    });
  };
  const userGender = JSON.parse(retrieveToken("user"));
  const aboutTexts = profile?.about_me;
  const basicDetails = getBasicDetails(profile);
  const currentLocationDetails = getCurrentLocationDetails(profile);
  const permanentAddressDetails = getPermanentAddressDetails(profile);
  const [formData, setFormData] = useState(() =>
    Object.fromEntries(basicDetails.map((item) => [item.label, item.value])),
  );
  const [locationData, setLocationData] = useState(() =>
    Object.fromEntries(
      currentLocationDetails.map((item) => [item.label, item.value]),
    ),
  );
  const [permanentAddressData, setPermanentAddressData] = useState(() =>
    Object.fromEntries(
      permanentAddressDetails.map((item) => [item.label, item.value]),
    ),
  );

  //api
  const { gender, diet, motherTongue, religion, nationality, maritalStatus } =
    useSelectOptions();
  const { data: communityData, isLoading: isCommunityPending } = useCommunities(
    {
      search: "",
      parent_id: formData?.Religion?.id ?? formData?.Religion,
    }
  );
  const selectCommunity = mapToSelectOptions(communityData);
  const { data: stateData, isLoading: isStatePending } = useStates({
    search: "",
    country_id: editing.basic
      ? (formData?.Nationality?.id ?? formData?.Nationality)
      : editing.location
        ? (locationData?.Country?.id ?? locationData?.Country)
        : (permanentAddressData?.Country?.id ?? permanentAddressData?.Country),
  });
  const selectState = mapToSelectOptions(stateData);
  const { data: districtData, isLoading: isDistrictPending } = useDistrict({
    search: "",
    parent_id: editing.location
      ? (locationData?.State?.id ?? locationData?.State)
      : (permanentAddressData?.State?.id ?? permanentAddressData?.State),
  });
  const selectDistrict = mapToSelectOptions(districtData);
  const { data: townData, isLoading: isTownPending } = useTown({
    search: "",
    place_id: editing.location
      ? (locationData?.District?.id ?? locationData?.District)
      : (permanentAddressData?.District?.id ?? permanentAddressData?.District),
  });
  const selectTown = mapToSelectOptions(townData);

  const basicSelectOptionMap = {
    Religion: religion?.options,
    Nationality: nationality?.options,
    Gender: gender?.options,
    Diet: diet?.options,
    "Mother Tongue": motherTongue?.options,
    Drinking: drinking,
    Smoking: smoking,
    Community: selectCommunity,
    "Interested in moving abroad":
      selectEditOptions["Interested in moving abroad"],
    "Marital status": maritalStatus?.options,
    "Grew up in": selectState,
    Language: motherTongue?.options,
  };

  const locationSelectOptionMap = {
    Country: nationality?.options,
    State: selectState,
    District: selectDistrict,
    Town: selectTown,
  };

  // In the toggleEdit function, when saving basic details:
  const toggleEdit = (field) => {
    setEditing((prev) => {
      const enteringEdit = !prev[field];

      // ... existing code ...

      if (!enteringEdit && field === "basic") {
        // When exiting edit mode (saving), clear permanent address district
        editProfileMutation.mutate(prepareBasicPayload(), {
          onSuccess: () => {
            setEditing((prev) => ({ ...prev, basic: false }));

            // Clear District in Permanent Address
            setPermanentAddressData((prev) => ({
              ...prev,
              District: null,
              Town: null,
              Pincode: "",
            }));

            queryClient.invalidateQueries({
              queryKey: ["profile-completion"],
            });
            queryClient.invalidateQueries({
              queryKey: ["my-profile"],
            });
          },
        });
        return prev; // Don't toggle yet, wait for success
      }

      return { ...prev, [field]: !prev[field] };
    });
  };
  const [aboutApproved, setAboutApproved] = useState("");
  const [aboutDraft, setAboutDraft] = useState("");

  useEffect(() => {
    if (aboutTexts) {
      setAboutApproved(aboutTexts);
      setAboutDraft(aboutTexts);
    }
  }, [profile]);

  const nonEditableFields = [
    "First Name",
    "Last Name",
    "Religion",
    "Community",
    "Gender",
  ];

  useEffect(() => {
    if (profile) {
      setFormData(
        Object.fromEntries(
          getBasicDetails(profile, lifestyleMap).map((item) => [
            item.label,
            item.value,
          ])
        )
      );
      setLocationData(
        Object.fromEntries(
          getCurrentLocationDetails(profile).map((item) => [
            item.label,
            item.value,
          ])
        )
      );
      setPermanentAddressData(
        Object.fromEntries(
          getPermanentAddressDetails(profile).map((item) => [
            item.label,
            item.value,
          ])
        )
      );
    }
  }, [profile]);

  const resetPermanentAddressDependencies = () => {
    setPermanentAddressData((prev) => ({
      ...prev,
      District: null,
      Town: null,
      Pincode: "",
    }));
  };

  const resetRules = {
    Religion: (prev) => ({ ...prev, Community: null }),

    // Add this new rule
    "Grew up in": (prev) => {
      // Clear District in Permanent Address
      setPermanentAddressData((prevPerm) => ({
        ...prevPerm,
        District: null,
        Town: null, // Also clear Town since it depends on District
        Pincode: "", // Clear Pincode as well
      }));
      return prev;
    },

    Country_location: (prev) => ({
      ...prev,
      State: null,
      District: null,
      Town: null,
      Pincode: "",
    }),
    State_location: (prev) => ({
      ...prev,
      District: null,
      Town: null,
      Pincode: "",
    }),
    District_location: (prev) => ({ ...prev, Town: null, Pincode: "" }),
    Town_location: (prev) => ({ ...prev, Pincode: "" }),

    Country_permanentAddress: (prev) => ({
      ...prev,
      State: null,
      District: null,
      Town: null,
      Pincode: "",
    }),
    State_permanentAddress: (prev) => ({
      ...prev,
      District: null,
      Town: null,
      Pincode: "",
    }),
    District_permanentAddress: (prev) => ({ ...prev, Town: null, Pincode: "" }),
    Town_permanentAddress: (prev) => ({ ...prev, Pincode: "" }),
  };

  const handleChange = (section, label, value) => {
    const updater =
      section === "basic"
        ? setFormData
        : section === "location"
          ? setLocationData
          : setPermanentAddressData;

    const newValue = value?.target ? value.target.value : value;

    // Apply reset if rule exists
    const key = `${label}${section !== "basic" ? "_" + section : ""}`;
    if (resetRules[key]) {
      updater(resetRules[key]);
    }

    // // Special case: When "Grew up in" changes, reset permanent address district
    // if (section === "basic" && label === "Grew up in") {
    //   resetPermanentAddressDependencies();
    // }

    // Always update the changed field
    updater((prev) => ({ ...prev, [label]: newValue }));
  };

  const editProfileMutation = useEditProfile();
  const prepareAboutPayload = () => ({
    about_me: aboutDraft,
  });

  const normalizeValue = (field) => {
    if (!field) return null;

    // {} or {id: undefined} or empty object
    if (typeof field === "object") {
      if (Array.isArray(field) && field.length === 0) return null;
      if (Object.keys(field).length === 0) return null;
      return field.id ?? null;
    }

    if (field === "") return null;

    return field;
  };

  const prepareBasicPayload = () => ({
    // Basic
    first_name: formData["First Name"] || "",
    last_name: formData["Last Name"] || "",
    marital_status: normalizeValue(formData["Marital status"]),
    religion: normalizeValue(formData["Religion"]),
    sub_religion: normalizeValue(formData["Community"]),
    gender: normalizeValue(formData["Gender"]),
    mother_tongue: normalizeValue(formData["Mother Tongue"]),
    language: Array.isArray(formData["Language"])
      ? formData["Language"].map((lang) => lang?.id ?? lang).filter(Boolean)
      : normalizeValue(formData["Language"])
        ? [normalizeValue(formData["Language"])]
        : null,
    dob: formData["Date of Birth"]
      ? dayjs(formData["Date of Birth"], "DD-MM-YYYY").format("YYYY-MM-DD")
      : null,
    country: normalizeValue(formData["Nationality"]),
    custom_location: formData["Location"],
    city: formData["Grew up in"]?.id ? formData["Grew up in"]?.id : null,
    place:
      permanentAddressData["State"].id !== formData["Grew up in"]?.id
        ? null
        : formData["District"]?.id,
    town:
      permanentAddressData["State"].id !== formData["Grew up in"]?.id
        ? null
        : formData["Town"]?.id,
    pin:
      permanentAddressData["State"].id !== formData["Grew up in"]?.id
        ? null
        : formData["Pincode"]?.id,
    diet: normalizeValue(formData["Diet"]),
    smoke: normalizeValue(formData["Smoking"]),
    drink: normalizeValue(formData["Drinking"]),
    interested_in_moving_abroad:
      formData["Interested in moving abroad"] !== null
        ? Number(formData["Interested in moving abroad"])
        : null,
  });

  const prepareCurrentLocationPayload = () => ({
    country_living: normalizeValue(locationData["Country"]),
    living_city: normalizeValue(locationData["State"]),
    living_place: normalizeValue(locationData["District"]),
    living_town: normalizeValue(locationData["Town"]),
    living_pin: normalizeValue(locationData["Pincode"]),
  });

  const preparePermanentAddressPayload = () => ({
    country: normalizeValue(permanentAddressData["Country"]),
    city: normalizeValue(permanentAddressData["State"]),
    place: normalizeValue(permanentAddressData["District"]),
    town: normalizeValue(permanentAddressData["Town"]),
    pin: normalizeValue(permanentAddressData["Pincode"]),
  });

  const getOptions = (label, isLocation) => {
    const source = isLocation
      ? editing.permanentAddress
        ? editPermanentAddressDetails
        : editCurrentLocationDetails
      : selectEditOptions;

    if (isLocation) {
      return source[label]
        ? source[label].map((item) => ({ value: item, label: item }))
        : null;
    }

    const normalizedLabel = label.trim().toLowerCase();
    const foundKey = Object.keys(source).find(
      (key) => key.trim().toLowerCase() === normalizedLabel
    );
    return foundKey ? source[foundKey] : null;
  };
  const minAge = userGender?.gender === "Female" ? 18 : 21;
  const cutoffDate = dayjs().subtract(minAge, "year").endOf("day");

  return (
    <div id={id} className="about-panel-frame panel-frame">
      <div className="about-description flex flex-col gap-4">
        <div className="header flex items-center justify-between">
          <label className="about-label font-title-normal">About</label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing.about) {
                  editProfileMutation.mutate(prepareAboutPayload(), {
                    onSuccess: () => {
                      setEditing((prev) => ({ ...prev, about: false }));
                      queryClient.invalidateQueries({
                        queryKey: ["my-profile"],
                      });
                    },
                  });
                } else {
                  toggleEdit("about");
                }
              }}
              className="edit-button"
            >
              {editing.about ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div className="about-content">
          {!editing.about ? (
            <p className="about-text">{displayTextOrDash(aboutApproved)}</p>
          ) : (
            <CustomTextArea
              id="about"
              value={aboutDraft}
              onChange={(e) => setAboutDraft(e.target.value)}
              placeholder="Write about yourself..."
              inputClassname="about-input"
              maxLength={500}
            />
          )}
        </div>
      </div>

      <Divider className="divider" />
      <div className="basic-details">
        <div className="header flex items-center justify-between">
          <label className="basic-label font-title-normal">Basic</label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                // if (editing.basic) {
                //   editProfileMutation.mutate(prepareBasicPayload(), {
                //     onSuccess: () => {
                //       setEditing((prev) => ({ ...prev, basic: false }));
                //       queryClient.invalidateQueries({
                //         queryKey: ["profile-completion"],
                //       });
                //       queryClient.invalidateQueries({
                //         queryKey: ["my-profile"],
                //       });
                //     },
                //   });
                // }
                toggleEdit("basic");
              }}
              className="edit-button"
            >
              {editing.basic ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div className="content-grid flex flex-col gap-4">
          {!editing.basic
            ? basicDetails.map((item) => (
                <div
                  key={item.label}
                  className="item-frame flex flex-col gap-1"
                >
                  <span className="item-label">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`item-value ${
                        isFieldBlurred(item.label) ? "blur-text" : ""
                      }`}
                    >
                      {displayTextOrDash(
                        item.label === "Interested in moving abroad"
                          ? (booleanMap[item.value] ?? item.value)
                          : item.label === "Language"
                            ? Array.isArray(item.value)
                              ? item.value.map((lang) => lang?.name).join(", ")
                              : item.value?.name ?? item.value
                            : typeof item.value === "object"
                              ? item.value?.name
                              : item.value
                      )}
                    </span>
                    {isFieldBlurred(item.label) && (
                      <Tooltip title="You should update your details to see this">
                        <span className="">
                          <HelpIcon />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))
            : basicDetails.map((item) => {
                const options = getOptions(item.label, false);
                const isDisabled = nonEditableFields.includes(item.label);
                const isMultiSelect =
                  item.label === "Language" ? "multiple" : "";
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>

                    {isDisabled ? (
                      <span className="item-value">
                        {displayTextOrDash(
                          typeof formData[item.label] === "object"
                            ? formData[item.label]?.name
                            : formData[item.label]
                        )}
                      </span>
                    ) : item.label === "Date of Birth" ? (
                      <DatePicker
                        isDisabled
                        value={
                          formData[item.label]
                            ? dayjs(formData[item.label], "DD-MM-YYY")
                            : null
                        }
                        onChange={(val) =>
                          handleChange("basic", item.label, val)
                        }
                        disabledDate={(current) =>
                          current && current > cutoffDate
                        }
                        defaultValue={cutoffDate}
                        className="item-input-date"
                      />
                    ) : item.label === "Location" ? (
                      <Input
                        id="location"
                        label=""
                        value={formData["Location"] || ""}
                        onChange={(val) =>
                          handleChange("basic", "Location", val)
                        }
                        inputClassname="item-input"
                        placeholder="Enter location"
                      />
                    ) : options ? (
                      <Select
                        SkeletonHeight={24}
                        returnObject
                        label={""}
                        isDisabled={
                          item.label === "Community" ||
                          item.label === "Gender" ||
                          item.label === "Religion"
                        }
                        isLoading={
                          item.label === "Community" && isCommunityPending
                        }
                        options={basicSelectOptionMap[item.label]}
                        value={
                          item.label === "Interested in moving abroad"
                            ? formData[item.label] != null
                              ? basicSelectOptionMap[item.label]?.find(
                                  (opt) =>
                                    Number(opt.value) ===
                                    Number(
                                      formData[item.label]?.value ??
                                        formData[item.label]
                                    )
                                )
                              : null
                            : item.label === "Language"
                              ? Array.isArray(formData[item.label])
                                ? formData[item.label]
                                    .map((lang) =>
                                      basicSelectOptionMap[item.label]?.find(
                                        (opt) =>
                                          opt.value === lang?.id ||
                                          opt.value === lang,
                                      )
                                    )
                                    .filter(Boolean)
                                : formData[item.label]?.id
                                  ? basicSelectOptionMap[item.label]?.find(
                                      (opt) =>
                                        opt.value === formData[item.label]?.id
                                    )
                                  : null
                              : formData[item.label]?.id
                                ? basicSelectOptionMap[item.label]?.find(
                                    (opt) =>
                                      opt.value === formData[item.label]?.id
                                  )
                                : basicSelectOptionMap[item.label]?.find(
                                    (opt) =>
                                      opt.label?.toLowerCase() ===
                                      String(
                                        formData[item.label]
                                      )?.toLowerCase()
                                  )
                        }
                        onChange={(val) => {
                          if (item.label === "Interested in moving abroad") {
                            setFormData((prev) => ({
                              ...prev,
                              [item.label]: val != null ? (val?.id ?? val?.value ?? null) : null,
                            }));
                          } else {
                            handleChange("basic", item.label, val);
                          }
                          console.log("formData",formData)
                        }}
                        mode={isMultiSelect}
                        inputClassName="item-input-select"
                      />
                    ) : (
                      <Input
                        isDisabled={
                          item.label === "First Name" ||
                          item.label === "Last Name"
                        }
                        id={item.label}
                        label={""}
                        value={formData[item.label]}
                        onChange={(val) =>
                          handleChange("basic", item.label, val)
                        }
                        inputClassname="item-input"
                      />
                    )}
                  </div>
                );
              })}
        </div>
      </div>
      <Divider className="divider" />
      <div id="current-address" className="current-location">
        <div className="header flex items-center justify-between">
          <label className="basic-label font-title-normal">
            Current Address
          </label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing.location) {
                  editProfileMutation.mutate(prepareCurrentLocationPayload(), {
                    onSuccess: () => {
                      setEditing((prev) => ({ ...prev, location: false }));
                      queryClient.invalidateQueries({
                        queryKey: ["my-profile"],
                      });
                    },
                  });
                }
                toggleEdit("location");
              }}
              className="edit-button"
            >
              {editing.location ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div className="content-grid flex flex-col gap-4">
          {!editing.location
            ? currentLocationDetails.map((item) => {
                const rawValue =
                  typeof item.value === "object"
                    ? item.value?.name
                    : item.value;
                const hasValue =
                  rawValue !== null &&
                  rawValue !== undefined &&
                  rawValue !== "";
                const shouldBlur = hasValue && isFieldBlurred(item.label);
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`item-value ${shouldBlur ? "blur-text" : ""}`}
                      >
                        {displayTextOrDash(rawValue)}
                      </span>
                      {shouldBlur && (
                        <Tooltip title="You should update your details to see this">
                          <span>
                            <HelpIcon />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })
            : currentLocationDetails.map((item) => {
                const options = getOptions(item.label, true);
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    {options ? (
                      <Select
                        SkeletonHeight={24}
                        returnObject
                        isDisabled={
                          (item.label === "District" &&
                            !locationData["State"]) ||
                          (item.label === "Town" && !locationData["District"])
                        }
                        isLoading={
                          (item.label === "State" && isStatePending) ||
                          (item.label === "District" && isDistrictPending) ||
                          (item.label === "Town" && isCommunityPending)
                        }
                        label={""}
                        options={locationSelectOptionMap[item.label]}
                        value={
                          locationData[item.label]?.id
                            ? locationSelectOptionMap[item.label]?.find(
                                (opt) =>
                                  opt.value === locationData[item.label]?.id
                              )
                            : locationSelectOptionMap[item.label]?.find(
                                (opt) => opt.label === locationData[item.label]
                              )
                        }
                        onChange={(val) =>
                          handleChange("location", item.label, val)
                        }
                        inputClassName="item-input-select"
                      />
                    ) : (
                      <Input
                        maxLength={6}
                        id={item.label}
                        label={""}
                        value={locationData[item.label]}
                        onChange={(e) =>
                          handleChange("location", item.label, e.target.value)
                        }
                        inputClassname="item-input"
                      />
                    )}
                  </div>
                );
              })}
        </div>
      </div>
      <Divider className="divider" />
      <div id="permanent-address" className="permanent-address">
        <div className="header flex items-center justify-between">
          <label className="basic-label font-title-normal">
            Permanent Address
          </label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing.permanentAddress) {
                  editProfileMutation.mutate(preparePermanentAddressPayload(), {
                    onSuccess: () => {
                      setEditing((prev) => ({
                        ...prev,
                        permanentAddress: false,
                      }));
                      queryClient.invalidateQueries({
                        queryKey: ["my-profile"],
                      });
                    },
                  });
                }
                toggleEdit("permanentAddress");
              }}
              className="edit-button"
            >
              {editing.permanentAddress ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div className="content flex flex-col gap-4">
          {!hasPremium && !isEdit && !preview ? (
            <div className="upgrade-card flex flex-col gap-6">
              <p className="address-description">Same as current location.</p>
              <GoPremiumCard title="To unlock location and details" />
            </div>
          ) : (
            <div className="content-grid gap-4">
              {!editing.permanentAddress
                ? permanentAddressDetails.map((item) => (
                    <div
                      key={item.label}
                      className="item-frame flex flex-col gap-1"
                    >
                      <span className="item-label">{item.label}</span>
                      <span
                        className={`item-value ${
                          isFieldBlurred(`Permanent ${item.label}`)
                            ? "blur-text"
                            : ""
                        }`}
                      >
                        {displayTextOrDash(
                          typeof item.value === "object"
                            ? item.value?.name
                            : item.value
                        )}
                        {isFieldBlurred(`Permanent ${item.label}`) && (
                          <Tooltip title="You should update your details to see this">
                            <span className="">
                              <HelpIcon />
                            </span>
                          </Tooltip>
                        )}
                      </span>
                    </div>
                  ))
                : permanentAddressDetails.map((item) => {
                    const options = getOptions(item.label, true);
                    return (
                      <div
                        key={item.label}
                        className="item-frame flex flex-col gap-1"
                      >
                        <span className="item-label">{item.label}</span>
                        {options ? (
                          <Select
                            SkeletonHeight={24}
                            returnObject
                            isDisabled={
                              (item.label === "District" &&
                                !permanentAddressData["State"]) ||
                              (item.label === "Town" &&
                                !permanentAddressData["District"])
                            }
                            isLoading={
                              (item.label === "State" && isStatePending) ||
                              (item.label === "District" &&
                                isDistrictPending) ||
                              (item.label === "Town" && isCommunityPending)
                            }
                            label={""}
                            options={locationSelectOptionMap[item.label]}
                            value={
                              permanentAddressData[item.label]?.id
                                ? locationSelectOptionMap[item.label]?.find(
                                    (opt) =>
                                      opt.value ===
                                      permanentAddressData[item.label]?.id
                                  )
                                : locationSelectOptionMap[item.label]?.find(
                                    (opt) =>
                                      opt.label ===
                                      permanentAddressData[item.label]
                                  )
                            }
                            onChange={(val) =>
                              handleChange("permanentAddress", item.label, val)
                            }
                            inputClassName="item-input-select"
                          />
                        ) : (
                          <Input
                            maxLength={6}
                            id={item.label}
                            label={""}
                            value={permanentAddressData[item.label]}
                            onChange={(e) =>
                              handleChange(
                                "permanentAddress",
                                item.label,
                                e.target.value
                              )
                            }
                            inputClassname="item-input"
                          />
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AboutPanel;
